import { ChevronDown } from '@untitledui/icons'
import { useRef, useState, type ReactNode } from 'react'
import {
	Button as AriaButton,
	Dialog as AriaDialog,
	DialogTrigger as AriaDialogTrigger,
	Popover as AriaPopover,
} from 'react-aria-components'
import { Form, Link } from 'react-router'
import { UserDropdown } from '#app/components/user-dropdown.tsx'
import { cx } from '#app/utils/cx.ts'
import { useAppHref, useHostOrigins, useSiteHref } from '#app/utils/hosts.ts'
import { useOptionalUser, userHasRole } from '#app/utils/user.ts'
import { Button } from './base/buttons/button'
import { SpacebrainsLogo } from './foundations/logo/spacebrains-logo'
import { SpacebrainsLogoMinimal } from './foundations/logo/spacebrains-logo-minimal'

type HeaderNavItem = {
	label: string
	href?: string
	menu?: ReactNode
	/** Marks the site the visitor is currently on. */
	isCurrent?: boolean
	/** Draws attention with a pulsing dot. */
	hasIndicator?: boolean
}

/**
 * The three Spacebrains journeys. Two of them live on other hosts, so these are
 * plain links rather than router navigations.
 */
function useHeaderNavItems(): HeaderNavItem[] {
	const { marketing, agency } = useHostOrigins()

	return [
		{
			label: 'For Recruiters',
			href: useSiteHref(marketing),
			isCurrent: true,
		},
		{
			label: 'Recruitment Agency',
			href: useSiteHref(agency),
		},
		{
			label: 'Available Jobs',
			href: useSiteHref(agency, '/engineers'),
			hasIndicator: true,
		},
	]
}

const NavIndicator = () => (
	<span aria-hidden="true" className="relative ml-1 inline-flex size-1.5">
		<span className="bg-fg-brand-primary absolute inline-flex size-full animate-ping rounded-full opacity-75" />
		<span className="bg-fg-brand-primary relative inline-flex size-full rounded-full" />
	</span>
)

const MobileNavItem = (props: {
	className?: string
	label: string
	href?: string
	isCurrent?: boolean
	hasIndicator?: boolean
	children?: ReactNode
}) => {
	const [isOpen, setIsOpen] = useState(false)

	if (props.href) {
		return (
			<li>
				<a
					href={props.href}
					aria-current={props.isCurrent ? 'page' : undefined}
					className={cx(
						'text-md hover:bg-primary_hover flex items-center justify-between px-4 py-3 font-semibold',
						props.isCurrent ? 'text-brand-secondary' : 'text-primary',
					)}
				>
					<span className="flex items-center">
						{props.label}
						{props.hasIndicator && <NavIndicator />}
					</span>
				</a>
			</li>
		)
	}

	return (
		<li className="flex flex-col gap-0.5">
			<button
				aria-expanded={isOpen}
				onClick={() => setIsOpen(!isOpen)}
				className="text-md text-primary hover:bg-primary_hover flex w-full items-center justify-between px-4 py-3 font-semibold"
			>
				{props.label}{' '}
				<ChevronDown
					className={cx(
						'text-fg-quaternary size-4 stroke-[2.625px] transition duration-100 ease-linear',
						isOpen ? '-rotate-180' : 'rotate-0',
					)}
				/>
			</button>
			{isOpen && <div>{props.children}</div>}
		</li>
	)
}

interface HeaderProps {
	items?: HeaderNavItem[]
	isFullWidth?: boolean
	isFloating?: boolean
	className?: string
}

export const Header = ({
	items,
	isFullWidth,
	isFloating,
	className,
}: HeaderProps) => {
	const headerRef = useRef<HTMLElement>(null)
	const formRef = useRef<HTMLFormElement>(null)
	const user = useOptionalUser()
	const navItems = useHeaderNavItems()
	const homeHref = useSiteHref(useHostOrigins().marketing)
	const loginHref = useAppHref('/login')
	const signupHref = useAppHref('/signup')
	const profileHref = useAppHref('/settings/profile')
	const adminHref = useAppHref('/admin')
	const resolvedItems = items ?? navItems

	return (
		<>
			<header
				ref={headerRef}
				className={cx(
					'bg-primary relative flex h-18 w-full items-center justify-center md:h-20',
					isFloating && 'h-16 md:h-19 md:pt-3',
					isFullWidth && !isFloating
						? 'has-aria-expanded:bg-primary'
						: 'max-md:has-aria-expanded:bg-primary',
					className,
				)}
			>
				<nav className="max-w-container mx-auto flex size-full flex-1 items-center pr-3 pl-4 md:px-8">
					<div
						className={cx(
							'flex w-full justify-between gap-4',
							isFloating &&
								'ring-secondary_alt md:bg-primary md:rounded-2xl md:py-3 md:pr-3 md:pl-4 md:shadow-xs md:ring-1',
						)}
					>
						<div className="flex flex-1 items-center gap-5">
							<Link to={homeHref} aria-label="Home">
								<SpacebrainsLogo className="h-6 md:max-lg:hidden" />
								<SpacebrainsLogoMinimal className="hidden h-6 md:inline-block lg:hidden" />
							</Link>

							{/* Desktop navigation */}
							<nav className="max-md:hidden">
								<ul className="flex items-center gap-0.5">
									{resolvedItems.map((navItem) => (
										<li key={navItem.label}>
											{navItem.menu ? (
												<AriaDialogTrigger>
													<AriaButton className="text-md text-secondary outline-focus-ring hover:text-secondary_hover flex cursor-pointer items-center gap-0.5 rounded-lg px-1.5 py-1 font-semibold transition duration-100 ease-linear focus-visible:outline-2 focus-visible:outline-offset-2">
														<span className="px-0.5">{navItem.label}</span>

														<ChevronDown className="text-fg-quaternary size-4 rotate-0 stroke-[2.625px] transition duration-100 ease-linear in-aria-expanded:-rotate-180" />
													</AriaButton>

													<AriaPopover
														className={({ isEntering, isExiting }) =>
															cx(
																'hidden origin-top will-change-transform md:block',
																isFullWidth && 'w-full',
																isEntering &&
																	'animate-in fade-in slide-in-from-top-1 duration-200 ease-out',
																isExiting &&
																	'animate-out fade-out slide-out-to-top-1 duration-150 ease-in',
															)
														}
														offset={isFloating || isFullWidth ? 0 : 8}
														containerPadding={0}
														triggerRef={
															(isFloating && isFullWidth) || isFullWidth
																? headerRef
																: undefined
														}
													>
														{({ isEntering, isExiting }) => (
															<AriaDialog
																className={cx(
																	'mx-auto origin-top outline-hidden',
																	isFloating && 'max-w-7xl px-8 pt-3',
																	// Have to use the scale animation inside the popover to avoid
																	// miscalculating the popover's position when opening.
																	isEntering &&
																		!isFullWidth &&
																		'animate-in zoom-in-95 duration-200 ease-out',
																	isExiting &&
																		!isFullWidth &&
																		'animate-out zoom-out-95 duration-150 ease-in',
																)}
															>
																{navItem.menu}
															</AriaDialog>
														)}
													</AriaPopover>
												</AriaDialogTrigger>
											) : (
												<a
													href={navItem.href}
													aria-current={navItem.isCurrent ? 'page' : undefined}
													className={cx(
														'text-secondary outline-focus-ring hover:text-secondary_hover flex cursor-pointer items-center gap-0.5 rounded-lg px-1.5 py-1 text-sm font-semibold transition duration-100 ease-linear focus:outline-offset-2 focus-visible:outline-2',
													)}
												>
													<span className="px-0.5">{navItem.label}</span>
													{navItem.hasIndicator && <NavIndicator />}
												</a>
											)}
										</li>
									))}
								</ul>
							</nav>
						</div>

						<div className="hidden items-center gap-3 md:flex">
							{user ? (
								<UserDropdown />
							) : (
								<>
									<Button
										color="secondary"
										size={isFloating ? 'md' : 'lg'}
										href={loginHref}
									>
										Log in
									</Button>
									<Button
										color="primary"
										size={isFloating ? 'md' : 'lg'}
										href={signupHref}
									>
										Get beta access
									</Button>
								</>
							)}
						</div>

						{/* Mobile menu and menu trigger */}
						<AriaDialogTrigger>
							<AriaButton
								aria-label="Toggle navigation menu"
								className={({ isFocusVisible, isHovered }) =>
									cx(
										'group ml-auto cursor-pointer rounded-lg p-2 md:hidden',
										isHovered && 'bg-primary_hover',
										isFocusVisible &&
											'outline-focus-ring outline-2 outline-offset-2',
									)
								}
							>
								<svg
									aria-hidden="true"
									width="24"
									height="24"
									viewBox="0 0 24 24"
									fill="none"
								>
									<path
										className="text-secondary hidden group-aria-expanded:block"
										d="M18 6L6 18M6 6L18 18"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
									<path
										className="text-secondary group-aria-expanded:hidden"
										d="M3 12H21M3 6H21M3 18H21"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
							</AriaButton>
							<AriaPopover
								triggerRef={headerRef}
								className="h-calc(100%-72px) scrollbar-hide w-full overflow-y-auto shadow-lg md:hidden"
								offset={0}
								crossOffset={20}
								containerPadding={0}
								placement="bottom left"
							>
								<AriaDialog className="outline-hidden">
									<nav className="bg-primary w-full shadow-lg">
										<ul className="flex flex-col gap-0.5 py-5">
											{resolvedItems.map((navItem) =>
												navItem.menu ? (
													<MobileNavItem
														key={navItem.label}
														label={navItem.label}
													>
														{navItem.menu}
													</MobileNavItem>
												) : (
													<MobileNavItem
														key={navItem.label}
														label={navItem.label}
														href={navItem.href}
														isCurrent={navItem.isCurrent}
														hasIndicator={navItem.hasIndicator}
													/>
												),
											)}
											{user ? (
												<>
													<MobileNavItem label="Profile" href={profileHref} />
													{userHasRole(user, 'admin') && (
														<MobileNavItem label="Admin" href={adminHref} />
													)}
													<li>
														<button
															onClick={() => formRef.current?.submit()}
															className="text-md text-primary hover:bg-primary_hover flex w-full items-center justify-between px-4 py-3 font-semibold"
														>
															Log out
														</button>
													</li>
												</>
											) : (
												<>
													<MobileNavItem label="Log in" href={loginHref} />
													<MobileNavItem
														label="Get beta access"
														href={signupHref}
													/>
												</>
											)}
										</ul>
									</nav>
								</AriaDialog>
							</AriaPopover>
						</AriaDialogTrigger>
					</div>
				</nav>
			</header>
			<Form action="/logout" method="POST" ref={formRef} />
		</>
	)
}
